"use client";

import { useEffect, useState, useMemo } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import {
  FileText,
  CheckCircle2,
  Calendar as CalendarIcon,
  CalendarPlus,
  ListTodo,
  AlertCircle,
  Clock,
  ArrowRight,
  Plus,
  Users,
  Sparkles,
  ChevronRight,
  FilePlus,
  FolderOpen,
} from "lucide-react";
import { apiRequest } from "@/lib/api";
import { toast } from "@/components/ui/toast";
import { useWorkspace } from "@/lib/workspace-context";
import { useTasksStore, type Task } from "@/stores/tasks-store";
import { useMeetingsStore, type Meeting } from "@/stores/meetings-store";
import { SkeletonCard } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { OnboardingGuide } from "@/components/onboarding";
import { useOnboarding } from "@/lib/use-onboarding";
import { cn } from "@/lib/utils";

interface ApprovalRequest {
  id: string;
  document_id: string;
  document_title: string | null;
  status: string;
  steps: { id: string; status: string; approver_id: string; approver_name: string | null; step_order: number }[];
  created_at: string;
}

export default function DashboardPage() {
  const router = useRouter();
  const { files, openFile, createFile, toggleTask, workspaceHistory } = useWorkspace();
  const onboardingState = useOnboarding();
  const tasksStore = useTasksStore();
  const meetingsStore = useMeetingsStore();

  const [approvals, setApprovals] = useState<ApprovalRequest[]>([]);
  const [approvalsLoading, setApprovalsLoading] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    tasksStore.fetchSummary();
    tasksStore.fetchTasks({ status: "todo" });
    meetingsStore.fetchSummary();
    meetingsStore.fetchMeetings();

    (async () => {
      try {
        const data = await apiRequest<ApprovalRequest[] | { items: ApprovalRequest[] }>(
          "/v1/workflows/approvals",
        );
        const list = Array.isArray(data) ? data : data?.items || [];
        setApprovals(list);
      } catch {
        // silent
      } finally {
        setApprovalsLoading(false);
      }
    })();
  }, []);

  const recentFiles = useMemo(() => {
    return files
      .filter((f) => f.type === "file")
      .sort((a, b) => (b.lastModified || "").localeCompare(a.lastModified || ""))
      .slice(0, 5);
  }, [files]);

  const lastFile = useMemo(() => {
    return [...files]
      .filter((f) => f.type === "file")
      .sort((a, b) => (b.lastModified || "").localeCompare(a.lastModified || ""))[0];
  }, [files]);

  const myOpenTasks = useMemo(() => {
    return tasksStore.tasks.filter((t) => t.status !== "done").slice(0, 5);
  }, [tasksStore.tasks]);

  const upcomingMeetings = useMemo(() => {
    const now = Date.now();
    return meetingsStore.meetings
      .filter((m) => m.status !== "cancelled" && new Date(m.starts_at).getTime() >= now - 3600_000)
      .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime())
      .slice(0, 3);
  }, [meetingsStore.meetings]);

  const myPendingApprovals = useMemo(() => {
    return approvals
      .filter((a) => a.status === "pending" || a.status === "in_review")
      .slice(0, 3);
  }, [approvals]);

  if (!mounted) {
    return (
      <div className="p-6 md:p-8 max-w-6xl mx-auto">
        <SkeletonCard />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }

  const hasAnyData = recentFiles.length + myOpenTasks.length + upcomingMeetings.length + myPendingApprovals.length > 0;

  // Hero greeting based on time of day
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 6) return "Buenas noches";
    if (h < 12) return "Buenos días";
    if (h < 19) return "Buenas tardes";
    return "Buenas noches";
  })();

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-6">
      {/* Hero greeting */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            {greeting}
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5 text-pretty">
            Aquí está lo que está pasando en tu workspace hoy.
          </p>
        </div>
        <button
          onClick={() => {
            const newId = createFile("Nota sin título");
            openFile(newId, "left");
          }}
          className="flex items-center gap-1.5 h-9 px-4 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90 active:scale-[0.98] transition-all shadow-sm shadow-primary/10"
        >
          <Plus className="w-3.5 h-3.5" />
          Nueva nota
        </button>
      </div>

      {/* Inline Onboarding Guide */}
      <OnboardingGuide state={onboardingState} />

      {/* Continúa donde quedaste */}
      {lastFile && (
        <button
          onClick={() => openFile(lastFile.id, "left")}
          className="w-full flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 hover:border-primary/40 hover:from-primary/10 hover:to-primary/15 transition-all group text-left"
        >
          <div className="w-12 h-12 rounded-xl bg-primary/15 border border-primary/25 flex items-center justify-center text-primary shrink-0 group-hover:scale-105 transition-transform">
            <Clock className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-primary/70 mb-0.5">
              Continúa donde quedaste
            </p>
            <p className="text-base font-bold text-foreground truncate">
              {lastFile.name.replace(".md", "")}
            </p>
            {lastFile.lastModified && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Última edición: {lastFile.lastModified}
              </p>
            )}
          </div>
          <ArrowRight className="w-5 h-5 text-primary/60 group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0" />
        </button>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <QuickAction
          label="Nueva nota"
          description="Crear documento en blanco"
          icon={FilePlus}
          onClick={() => {
            const newId = createFile("Nota sin título");
            openFile(newId, "left");
          }}
          color="primary"
        />
        <QuickAction
          label="Nueva reunión"
          description="Agendar evento en el calendario"
          icon={CalendarPlus}
          onClick={() => router.push("/workspace/meetings")}
          color="blue"
        />
        <QuickAction
          label="Nueva tarea"
          description="Añadir tarea al tablero Kanban"
          icon={ListTodo}
          onClick={() => router.push("/workspace/tasks")}
          color="amber"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Tareas abiertas"
          value={myOpenTasks.length}
          icon={ListTodo}
          href="/workspace/tasks"
          color="text-amber-500"
          loading={tasksStore.loading}
          topItems={myOpenTasks.slice(0, 5).map((t) => ({ id: t.id, title: t.title }))}
        />
        <StatCard
          label="Próximas reuniones"
          value={upcomingMeetings.length}
          icon={CalendarIcon}
          href="/workspace/meetings"
          color="text-blue-500"
          loading={meetingsStore.loading}
        />
        <StatCard
          label="Aprobaciones"
          value={myPendingApprovals.length}
          icon={CheckCircle2}
          href="/workspace/workflows"
          color="text-emerald-500"
          loading={approvalsLoading}
        />
        <StatCard
          label="Documentos"
          value={files.filter((f) => f.type === "file").length}
          icon={FileText}
          href="/workspace/spaces"
          color="text-primary"
        />
      </div>

      {!hasAnyData && (
        <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 via-primary/3 to-transparent p-6 md:p-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
              <Sparkles className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">¡Bienvenido a Arsenal Wiki!</h2>
              <p className="text-xs text-muted-foreground">
                Elige una opción para empezar
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
            <button
              onClick={() => {
                const newId = createFile("Nota sin título");
                openFile(newId, "left");
              }}
              className="p-5 rounded-xl bg-card border border-border/40 hover:border-primary/40 hover:bg-primary/5 transition-all text-left group"
            >
              <FilePlus className="w-6 h-6 text-primary mb-2 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <p className="text-sm font-semibold text-foreground">Empezar con nota en blanco</p>
              <p className="text-[11px] text-muted-foreground mt-1">
                Crea un documento vacío y escribe lo que quieras
              </p>
            </button>
            <Link
              href="/workspace/templates"
              className="p-5 rounded-xl bg-card border border-border/40 hover:border-primary/40 hover:bg-primary/5 transition-all text-left group"
            >
              <FileText className="w-6 h-6 text-primary mb-2 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <p className="text-sm font-semibold text-foreground">Usar una plantilla</p>
              <p className="text-[11px] text-muted-foreground mt-1">
                Estructuras prediseñadas: OKRs, RFC, retro, etc.
              </p>
            </Link>
            <Link
              href="/workspace/spaces"
              className="p-5 rounded-xl bg-card border border-border/40 hover:border-primary/40 hover:bg-primary/5 transition-all text-left group"
            >
              <FolderOpen className="w-6 h-6 text-primary mb-2 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <p className="text-sm font-semibold text-foreground">Crear un proyecto</p>
              <p className="text-[11px] text-muted-foreground mt-1">
                Organiza tus notas en espacios temáticos
              </p>
            </Link>
          </div>
        </div>
      )}

      {recentFiles.length > 0 && (
        <Section
          title="Documentos recientes"
          icon={FileText}
          href="/workspace"
          linkLabel="Ir al workspace"
        >
          <div className="space-y-1">
            {recentFiles.map((file) => (
              <button
                key={file.id}
                onClick={() => openFile(file.id, "left")}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/40 transition-colors group text-left"
              >
                <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">
                    {file.name.replace(".md", "")}
                  </div>
                  {file.project && (
                    <div className="text-xs text-muted-foreground truncate">
                      {file.project}
                    </div>
                  )}
                </div>
                {file.lastModified && (
                  <span className="text-xs text-muted-foreground/60 shrink-0">
                    {file.lastModified}
                  </span>
                )}
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
          </div>
        </Section>
      )}

      {workspaceHistory.length > 0 && (
        <Section title="Actividad reciente del equipo" icon={Sparkles}>
          <div className="space-y-1">
            {workspaceHistory.slice(0, 6).map((entry) => (
              <div
                key={entry.id}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary/40 transition-colors"
              >
                <div className="w-7 h-7 rounded-full bg-primary/15 text-primary text-[10px] font-bold flex items-center justify-center shrink-0">
                  {(entry.user || "A").slice(0, 2).toUpperCase()}
                </div>
                <p className="text-xs text-foreground/90 flex-1 min-w-0 truncate">
                  <span className="font-semibold">{entry.user || "Alguien"}</span>
                  {" "}{entry.action.toLowerCase()}{" "}
                  <span className="font-semibold text-foreground">{entry.nodeName}</span>
                </p>
                <span className="text-[10px] text-muted-foreground/60 shrink-0">
                  {new Date(entry.timestamp).toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {myOpenTasks.length > 0 && (
          <Section
            title="Mis tareas"
            icon={ListTodo}
            href="/workspace/tasks"
            linkLabel="Ver todas"
          >
            <div className="space-y-1">
              {myOpenTasks.map((task) => (
                <TaskRow key={task.id} task={task} onToggle={() => toggleTask(task.id)} />
              ))}
            </div>
          </Section>
        )}

        {upcomingMeetings.length > 0 && (
          <Section
            title="Próximas reuniones"
            icon={CalendarIcon}
            href="/workspace/meetings"
            linkLabel="Ver todas"
          >
            <div className="space-y-1">
              {upcomingMeetings.map((meeting) => (
                <MeetingRow key={meeting.id} meeting={meeting} />
              ))}
            </div>
          </Section>
        )}
      </div>

      {myPendingApprovals.length > 0 && (
        <Section
          title="Aprobaciones pendientes"
          icon={CheckCircle2}
          href="/workspace/workflows"
          linkLabel="Ver workflows"
        >
          <div className="space-y-1">
            {myPendingApprovals.map((req) => (
              <Link
                key={req.id}
                href="/workspace/workflows"
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/40 transition-colors group"
              >
                <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                  <Clock className="w-3.5 h-3.5 text-amber-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">
                    {req.document_title || "Documento sin título"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {req.steps.length} paso{req.steps.length !== 1 ? "s" : ""} · {req.status}
                  </div>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function QuickAction({
  label,
  description,
  icon: Icon,
  onClick,
  color,
}: {
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  onClick: () => void;
  color: "primary" | "blue" | "amber";
}) {
  const colorMap = {
    primary: "bg-primary/10 text-primary border-primary/20 hover:bg-primary/20",
    blue: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20 hover:bg-blue-500/20",
    amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 hover:bg-amber-500/20",
  };
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 p-3.5 rounded-xl border transition-all text-left group",
        colorMap[color],
      )}
    >
      <div className="w-10 h-10 rounded-lg bg-background/60 border border-border/40 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
        <Icon className="w-4.5 h-4.5" strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground">{label}</p>
        <p className="text-[11px] text-muted-foreground">{description}</p>
      </div>
      <Plus className="w-4 h-4 text-muted-foreground/40 group-hover:text-foreground group-hover:rotate-90 transition-all shrink-0" />
    </button>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  href,
  color,
  loading,
  topItems,
}: {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  color: string;
  loading?: boolean;
  topItems?: { id: string; title: string }[];
}) {
  return (
    <div className="relative group/stat">
      <Link
        href={href}
        className="block p-4 rounded-xl bg-card border border-border/40 hover:border-primary/30 transition-all"
        aria-label={`${label}: ${loading ? "cargando" : value}. Ir a ${href}`}
      >
        <div className="flex items-center justify-between mb-2">
          <Icon className={cn("w-4 h-4", color)} aria-hidden="true" />
          <ArrowRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover/stat:opacity-100 transition-opacity" aria-hidden="true" />
        </div>
        <div className="text-2xl font-bold text-foreground tabular-nums" aria-hidden="true">
          {loading ? "—" : value}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
      </Link>
      {topItems && topItems.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 z-20 hidden group-hover/stat:block">
          <div className="p-2 rounded-lg bg-card border border-border/60 shadow-xl">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 px-1.5 pt-0.5 pb-1">
              Top {Math.min(topItems.length, 5)}
            </p>
            {topItems.slice(0, 5).map((item) => (
              <Link
                key={item.id}
                href={href}
                className="block px-1.5 py-1 rounded text-[11px] text-foreground hover:bg-secondary/60 truncate"
              >
                {item.title}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  href,
  linkLabel,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  href?: string;
  linkLabel?: string;
  children: React.ReactNode;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-5 rounded-xl bg-card border border-border/40"
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Icon className="w-4 h-4 text-muted-foreground" />
          {title}
        </h2>
        {href && linkLabel && (
        <Link
          href={href}
          className="text-xs font-medium text-primary hover:text-primary/80 flex items-center gap-1"
        >
          {linkLabel}
          <ArrowRight className="w-3 h-3" />
        </Link>
        )}
      </div>
      {children}
    </motion.div>
  );
}

function TaskRow({ task, onToggle }: { task: Task; onToggle: () => void }) {
  const priorityColor: Record<string, string> = {
    high: "bg-red-500/10 text-red-500 border-red-500/20",
    medium: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    low: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  };
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary/40 transition-colors group">
      <button
        onClick={onToggle}
        className="w-4 h-4 rounded border border-border flex items-center justify-center hover:border-primary transition-colors shrink-0"
        aria-label="Marcar como completada"
      >
        {task.status === "done" && <CheckCircle2 className="w-3 h-3 text-primary" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-foreground truncate">{task.title}</div>
        {task.due_date && (
          <div className="text-xs text-muted-foreground">
            Vence: {new Date(task.due_date).toLocaleDateString()}
          </div>
        )}
      </div>
      {task.priority && (
        <span
          className={cn(
            "text-[10px] font-medium px-2 py-0.5 rounded-full border",
            priorityColor[task.priority],
          )}
        >
          {task.priority}
        </span>
      )}
    </div>
  );
}

function MeetingRow({ meeting }: { meeting: Meeting }) {
  const startDate = new Date(meeting.starts_at);
  const now = new Date();
  const isToday = startDate.toDateString() === now.toDateString();
  const isTomorrow =
    startDate.toDateString() === new Date(now.getTime() + 86400_000).toDateString();

  const dateLabel = isToday
    ? `Hoy, ${startDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
    : isTomorrow
      ? `Mañana, ${startDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
      : startDate.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <Link
      href="/workspace/meetings"
      className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/40 transition-colors group"
    >
      <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
        <CalendarIcon className="w-3.5 h-3.5 text-blue-500" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground truncate">
          {meeting.title}
        </div>
        <div className="text-xs text-muted-foreground">{dateLabel}</div>
      </div>
      {meeting.attendees.length > 0 && (
        <span className="text-xs text-muted-foreground/60 flex items-center gap-1">
          <Users className="w-3 h-3" />
          {meeting.attendees.length}
        </span>
      )}
    </Link>
  );
}
